import React, { useState, useEffect } from 'react'
import { bool, number, oneOfType, shape, string } from 'prop-types'
import { css } from '@emotion/core'
import { HeaderBlock, Body1, PersonBlock, useAtSize } from '@fs/zion-ui'
import { trackEvent } from '@fs/zion-analytics'
import { useTranslation } from 'react-i18next'
import { shuffle } from '../../../../../shared/helpers/utility'
import { useConfetti } from '../../../../../shared/helpers/specialEffects_helper'
import getRelationshipStringKeyFromAscendancyNumber from '../../../../../shared/helpers/relationship_helper'
import { extractFourDigitYear } from '../../../../../shared/helpers/factDate_Helper'

export default function Quiz({ id, gender, twoColumnQuestions, showPersonBlock, thumbnailIcon, year, quiz, person }) {
  const [t] = useTranslation()
  const atSize = useAtSize()
  const [selectedAnswer, setSelectedAnswer] = useState()
  const [options, setOptions] = useState([])
  const [feedbackMessage, setFeedbackMessage] = useState(t('aam.quiz.take-a-guess', 'Take a guess!'))
  const [answerExplanation, setAnswerExplanation] = useState('')
  const [extraContentSubHeader, setExtraContentSubHeader] = useState('')
  const [extraContentText, setExtraContentText] = useState('')
  const confetti = useConfetti()

  const mainStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    width: 100%;
  `

  const quizContainerDualColumnStyle = css`
    width: ${atSize({ default: '280px', lg: '560px' })};
    display: grid;
    grid-template-columns: ${atSize({ default: '100%', lg: '49% 49%' })};
    grid-gap: 2%;
    text-align: center;
  `

  const quizContainerSingleColumnStyle = css`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    text-align: center;
    margin-top: 15px;
  `
  const quizOptionStyle = css`
    background: #fff;
    transition: all 0.15s;
    border-radius: 35px;
    height: 35px;
    width: 100%;
    padding-left: 12px;
    padding-right: 12px;
    line-height: 1.2;
    display: flex;
    align-items: center;
    font-size: 16px;
    justify-content: center;
    cursor: pointer;
    user-select: none;
    box-sizing: border-box;
    margin-bottom: 20px;
    color: #212225;
    outline: none;
  `
  const mutedAnswerStyle = css`
    background-color: #fff;
  `
  const correctlyAnsweredStyle = css`
    background-color: #c3e070;
  `
  const incorrectlyAnsweredStyle = css`
    border: 3px solid #ef6d6d;
    background-color: #cc2529;
  `

  const resetQuizValues = () => {
    setFeedbackMessage('')
    setAnswerExplanation('')
    setExtraContentSubHeader('')
    setExtraContentText('')
    setSelectedAnswer(null)
  }

  useEffect(() => {
    if (quiz) {
      const answers = quiz.answers ? quiz.answers.split('|') : []
      const o = answers.map((elem, ind) => {
        return { answer: elem, isCorrect: ind === 0 }
      })
      setOptions(shuffle(o))
      resetQuizValues()
    }
  }, [quiz])

  const handleCorrectAnswer = () => {
    confetti(true)
    setFeedbackMessage(t('aam.quiz.correct', 'Correct!'))
    setAnswerExplanation(quiz.answerText)
    setExtraContentSubHeader(quiz.text2)
    setExtraContentText(quiz.text3 && quiz.text4 ? `${quiz.text3}<br/> ${quiz.text4}` : '')
  }

  const handleIncorrectAnswer = () => {
    confetti(false)
    setFeedbackMessage(t('aam.quiz.not-quite', 'Not quite.'))
    setAnswerExplanation('')
    setExtraContentSubHeader('')
    setExtraContentText('')
  }

  const handleAnswerClicked = (option) => {
    setSelectedAnswer(option)
    if (option.isCorrect) {
      handleCorrectAnswer()
    } else {
      handleIncorrectAnswer()
    }
    return true
  }

  const getSelectedQuizStyle = (dataItem, selected) => {
    let returnStyle

    if (selected && dataItem.answer === selected.answer && selected.isCorrect) {
      returnStyle = correctlyAnsweredStyle
    } else if (selected && dataItem.answer === selected.answer && !selected.isCorrect) {
      returnStyle = incorrectlyAnsweredStyle
    } else if (selected) {
      returnStyle = mutedAnswerStyle
    }
    return returnStyle
  }

  const singleColumnQuestionsStyle = css`
    width: 100%;
    max-width: 400px;
  `

  const birthYear = extractFourDigitYear(person?.dob)
  const age = year - birthYear
  const strType = age > 0 ? 'yearTurnedAge' : 'yearBorn'

  return (
    <>
      <div css={mainStyle}>
        <div css={!twoColumnQuestions ? singleColumnQuestionsStyle : null}>
          {showPersonBlock && (
            <div css={{ padding: atSize({ default: '10px', lg: '20px' }) }}>
              <PersonBlock
                size={atSize({ default: 'md', lg: 'lg', xl: 'xl' })}
                avatarProps={{
                  sex: gender,
                  src: thumbnailIcon,
                }}
                name={`${year}`}
                details={person?.atn && t(getRelationshipStringKeyFromAscendancyNumber(person?.atn, strType), { age })}
              />
            </div>
          )}

          <div css={twoColumnQuestions ? quizContainerDualColumnStyle : quizContainerSingleColumnStyle}>
            {quiz &&
              options.map((option, ind) => (
                <div
                  key={option.answer}
                  css={[quizOptionStyle, getSelectedQuizStyle(option, selectedAnswer)]}
                  role="button"
                  tabIndex={ind}
                  onClick={() => {
                    trackEvent({
                      action: 'click_action',
                      link_name: `DS_ALL_ABOUT_ME: DS_AAM_QUIZ_${id}_CORRECT_${option.isCorrect}`.toUpperCase(),
                    })
                    handleAnswerClicked(option)
                  }}
                  onKeyPress={() => {
                    handleAnswerClicked(option)
                  }}
                >
                  {option.answer}
                </div>
              ))}
          </div>
        </div>
        <div>
          <div>{quiz && <HeaderBlock heading={feedbackMessage} size="sm" />}</div>
          <div>
            <Body1>{answerExplanation}</Body1>
          </div>
          <div>
            <Body1>{extraContentSubHeader}</Body1>
          </div>
          <div>
            <Body1
              dangerouslySetInnerHTML={{
                __html: extraContentText,
              }}
            />
          </div>
        </div>
        {!quiz && (
          <HeaderBlock
            heading={t('aam.no.data.for.year', "We don't have data for the selected year")}
            size="md"
            centered
          />
        )}
      </div>
    </>
  )
}

Quiz.propTypes = {
  gender: string,
  twoColumnQuestions: bool,
  showPersonBlock: bool,
  thumbnailIcon: string,
  year: number,
  person: shape({
    atn: oneOfType([string, number]),
    dob: oneOfType([string, number]),
  }),

  quiz: shape({
    answerText: string,
    answers: string,
    id: number,
    language: string,
    quizType: string,
    region: string,
    text: string,
    text1: string,
    text2: string,
    text3: string,
    text4: string,
    year: number,
  }),
}
